import { HttpRequest } from '../../components/HttpRequest';
import { cartApi } from '../api/cart';
import { setPackage } from '../../features/cartSlice';
import secureLocalStorage from 'react-secure-storage';

class PackageService {
  /**
   * Fetch available packages from the API
   */
  async fetchPackages() {
    const businessNumber = secureLocalStorage.getItem('businessId');
    try {
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/packages-in-bs?businessNumber=${businessNumber}`
      });
      return response?.response?.data || [];
    } catch (error) {
      console.error('Error fetching packages:', error);
      return [];
    }
  }

  /**
   * Determine the best package based on total quantity
   */
  determineBestPackage(packages, totalQuantity) {
    if (!packages || packages.length === 0) return null;

    let bestPackage = null;

    for (const pkg of packages) {
      const packageCapacity = pkg?.noOfItemsInPackage;
      const packageStock = pkg?.pricing?.[0]?.availableStock || 0;

      // Check if package can fit the items and has stock
      if (packageCapacity >= totalQuantity &&
          packageStock > 0 &&
          (!bestPackage || packageCapacity < bestPackage?.noOfItemsInPackage)) {
        bestPackage = pkg;
      }
    }

    return bestPackage;
  }

  /**
   * Fetch and determine the best package for given quantity
   */
  async fetchAndDetermineBestPackage(additionalQuantity = 1) {
    try {
      // Get current cart items to calculate total quantity
      const currentCart = cartApi.getNonPackagingItems();
      const currentQuantity = currentCart.reduce((sum, item) => sum + (item?.quantity || 0), 0);
      const totalQuantity = currentQuantity + additionalQuantity;

      // Fetch available packages
      const packages = await this.fetchPackages();

      // Find the best package
      const bestPackage = this.determineBestPackage(packages, totalQuantity);

      return bestPackage;
    } catch (error) {
      console.error('Error in fetchAndDetermineBestPackage:', error);
      return null;
    }
  }

  /**
   * Create package payload for cart
   */
  createPackagePayload(selectedPackage) {
    if (!selectedPackage) return null;

    return {
      id: selectedPackage._id || selectedPackage.id,
      _id: selectedPackage._id || selectedPackage.id,
      name: selectedPackage.productName || selectedPackage.name,
      productName: selectedPackage.productName || selectedPackage.name,
      imageUrl: selectedPackage.featuredImage || selectedPackage.thumbailImagePath || selectedPackage.image,
      featuredImage: selectedPackage.featuredImage || selectedPackage.image,
      image: selectedPackage.featuredImage || selectedPackage.image,
      price: selectedPackage.pricing?.[0]?.discountedPrice || selectedPackage.price || 0,
      discountedPrice: selectedPackage.pricing?.[0]?.discountedPrice || selectedPackage.price,
      normalPrice: selectedPackage.pricing?.[0]?.normalPrice || selectedPackage.price,
      category: 'Packaging',
      stock: selectedPackage.pricing?.[0]?.availableStock || selectedPackage.availableStock || 0,
      isPackage: true,
      // Add variationId for order creation
      variationId: selectedPackage.pricing?.[0]?._id || selectedPackage._id || selectedPackage.id
    };
  }

  /**
   * Create package payload for Redux store
   */
  createReduxPayload(selectedPackage) {
    if (!selectedPackage) return null;

    return {
      id: selectedPackage._id || selectedPackage.id,
      image: selectedPackage.featuredImage || selectedPackage.image,
      name: selectedPackage.productName || selectedPackage.name,
      price: selectedPackage.pricing?.[0]?.discountedPrice || selectedPackage.price || 0,
      quantity: 1,
      variationId: selectedPackage.pricing?.[0]?._id,
      availableStock: selectedPackage.pricing?.[0]?.availableStock || selectedPackage.availableStock || 0
    };
  }

  /**
   * Assign package to order
   */
  async assignPackage(quantity, dispatch) {
    try {
      const selectedPackage = await this.fetchAndDetermineBestPackage(quantity);

      if (!selectedPackage) {
        // No suitable package found - this might be okay
        console.log('No suitable package found for quantity:', quantity);
        return { success: true, package: null };
      }

      const packageStock = selectedPackage?.pricing?.[0]?.availableStock ||
                          selectedPackage?.availableStock || 0;

      if (packageStock <= 0) {
        return {
          success: false,
          error: 'Selected package is out of stock',
          package: null
        };
      }

      // Create package payload for cart
      const cartPayload = this.createPackagePayload(selectedPackage);

      if (cartPayload) {
        // Add package to cart with isPackage flag
        cartApi.addToCart(cartPayload, 1, true);

        // Dispatch to Redux if dispatch function is provided
        if (dispatch) {
          const reduxPayload = this.createReduxPayload(selectedPackage);
          if (reduxPayload) {
            dispatch(setPackage(reduxPayload));
          }
        }

        return {
          success: true,
          package: selectedPackage,
          cartPayload,
          message: 'Package assigned successfully'
        };
      }

      return {
        success: false,
        error: 'Failed to create package payload',
        package: null
      };

    } catch (error) {
      console.error('Error assigning package:', error);
      return {
        success: false,
        error: error.message || 'Failed to assign package',
        package: null
      };
    }
  }

  /**
   * Remove package items from cart
   */
  removePackagesFromCart() {
    const packages = cartApi.getPackagingItems();
    packages.forEach(pkg => {
      if (pkg?.product?.id) {
        cartApi.removeFromCart(pkg.product.id);
      }
    });
  }

  /**
   * Get current package fee from cart
   */
  getPackageFee() {
    return cartApi.getPackagingTotal();
  }

  /**
   * Check if package is already in cart
   */
  hasPackageInCart() {
    const packages = cartApi.getPackagingItems();
    return packages.length > 0;
  }

  /**
   * Update package quantity if needed
   */
  async updatePackageIfNeeded(newTotalQuantity, dispatch) {
    // Remove existing packages
    this.removePackagesFromCart();

    // Assign new package based on new quantity
    return await this.assignPackage(newTotalQuantity, dispatch);
  }
}

const packageService = new PackageService();
export default packageService;